import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Tab,
    TabList,
    Tabs,
    Text,
    useBreakpointValue,
    useColorModeValue,
    VStack,
    Grid,
} from '@chakra-ui/react';
import {
    MdAdd,
    MdArrowForward,
    MdCalendarToday,
    MdChevronRight,
    MdRefresh,
    MdSearch,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import {
    UnifiedEmptyState,
    UnifiedFilterBar,
    UnifiedLoadingState,
    UnifiedSurfaceCard,
    useUnifiedInteractiveCardStyles,
    useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTabsStyles } from 'components/layout/useUnifiedUiStyles';
import { useTranslation } from 'i18n/TranslationProvider';
import { translateRaw } from 'i18n/globalTranslator';
import { useListing } from 'contexts/ListingContext';
import { getMyListings } from '../services/listingService';
import StatusBadge from '../components/StatusBadge';


const ACTIVE_STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'has_quotes', label: 'Has quotes' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'awaiting_payment', label: 'Awaiting payment' },
    { value: 'paid', label: 'Paid' },
    { value: 'planned', label: 'Planned' },
    { value: 'in_transit', label: 'In transit' },
    { value: 'pickedUp', label: 'Picked up' },
    { value: 'delivered', label: 'Delivered' },
];

const ARCHIVED_STATUS_OPTIONS = [
    { value: 'completed', label: 'Completed' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'expired', label: 'Expired' },
];

const ARCHIVED_STATUS_SET = new Set(ARCHIVED_STATUS_OPTIONS.map((option) => option.value));

function formatListingDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCategory(category) {
    return String(category || 'other').replace(/_/g, ' ');
}

function getItemSummary(listing) {
    const items = Array.isArray(listing.items) ? listing.items.filter(Boolean) : [];
    if (!items.length) return { count: 0, title: translateRaw('No items added yet'), facts: [] };

    const first = items[0];
    const title =
        [first.make, first.model].filter(Boolean).join(' ').trim() ||
        first.itemDescription ||
        first.petType ||
        first.category ||
        translateRaw(items.length > 1 ? 'Item set' : 'Item details');

    const facts = [];
    if (items.length) facts.push(items.length !== 1 ? translateRaw('{{count}} items', { count: items.length }) : translateRaw('{{count}} item', { count: items.length }));
    if (first.quantity) facts.push(translateRaw('Qty {{quantity}}', { quantity: first.quantity }));

    const rawStatus = String(first.status || first.vehicleStatus || '').toLowerCase();
    if (rawStatus === 'operable' || rawStatus === 'operational') facts.push(translateRaw('Operable'));
    if (rawStatus === 'non-operable' || rawStatus === 'nonoperable' || rawStatus === 'inoperable') facts.push(translateRaw('Non-operable'));

    const totalWeight = items.reduce((sum, item) => sum + (Number(item.weight || 0) * Number(item.quantity || 1)), 0);
    if (totalWeight > 0) {
        facts.push(translateRaw('Total weight: {{weight}} kg', { weight: totalWeight }));
    } else if (first.weight) {
        facts.push(translateRaw('{{weight}} kg', { weight: first.weight }));
    }

    if ([first.length, first.width, first.height].some(Boolean)) {
        facts.push(translateRaw('{{dimensions}} cm', { dimensions: [first.length, first.width, first.height].filter(Boolean).join(' × ') }));
    }

    return { count: items.length, title, facts: facts.slice(0, 4) };
}

function matchesSearch(listing, searchValue) {
    if (!searchValue) return true;
    const value = searchValue.trim().toLowerCase();
    if (!value) return true;

    return [
        listing.title,
        listing.humanId,
        listing.category,
        listing.fromCity,
        listing.fromCountry,
        listing.toCity,
        listing.toCountry,
        listing.description,
        listing.items?.[0]?.make,
        listing.items?.[0]?.itemDescription,
        listing.items?.[0]?.petType,
    ]
        .filter(Boolean)
        .some((entry) => String(entry).toLowerCase().includes(value));
}

const VisualRoute = ({ from, to, fromDate, toDate, distance }) => {
    const borderColor = useColorModeValue('brand.100', 'whiteAlpha.200');
    const dateColor = useColorModeValue('gray.500', 'whiteAlpha.600');
    const distanceColor = useColorModeValue('brand.500', 'brand.200');

    const bg = useColorModeValue('white', 'navy.800');

    return (
        <HStack flex="1" spacing={4} align="center" py={1}>
            <VStack align="start" spacing={0} flex="1">
                <HStack spacing={2} align="center">
                    <Flex align="center" justify="center" minW="18px" h="18px" borderRadius="full" bg="brand.500" color="white" fontSize="9px" fontWeight="900">A</Flex>
                    <Text fontWeight="800" fontSize="sm" noOfLines={1}>{from}</Text>
                </HStack>
                <Text fontSize="xs" color={dateColor} fontWeight="600" noOfLines={1} pl={6}>{fromDate}</Text>
            </VStack>

            <Box flex="0.6" position="relative" h="32px">
                {distance && (
                    <Text
                        position="absolute"
                        top="-6px"
                        left="50%"
                        transform="translateX(-50%)"
                        fontSize="10px"
                        fontWeight="900"
                        color={distanceColor}
                        whiteSpace="nowrap"
                        zIndex={1}
                        bg={bg}
                        px={2}
                        borderRadius="full"
                    >
                        {distance}
                    </Text>
                )}
                <Box
                    position="absolute"
                    top="20px"
                    left="0"
                    right="0"
                    h="1px"
                    borderBottom="2px dashed"
                    borderColor={borderColor}
                />
                <Icon
                    as={MdChevronRight}
                    position="absolute"
                    top="20px"
                    right="-4px"
                    transform="translateY(-50%)"
                    color="brand.400"
                    boxSize={4}
                />
            </Box>

            <VStack align="end" spacing={0} flex="1">
                <HStack spacing={2} align="center" justify="flex-end">
                    <Text fontWeight="800" fontSize="sm" textAlign="right" noOfLines={1}>{to}</Text>
                    <Flex align="center" justify="center" minW="18px" h="18px" borderRadius="full" bg="red.500" color="white" fontSize="9px" fontWeight="900">B</Flex>
                </HStack>
                <Text fontSize="xs" color={dateColor} fontWeight="600" textAlign="right" noOfLines={1} pr={6}>{toDate}</Text>
            </VStack>
        </HStack>
    );
}

// --- High-Density Table Cell Components ---

const RouteCell = ({ fromCity, fromPostcode, toCity, toPostcode, fromAddress, toAddress, orderId, postedDate }) => {
    const { t } = useTranslation();
    const mutedText = useColorModeValue('gray.500', 'whiteAlpha.600');
    const orderIdColor = useColorModeValue('brand.500', 'brand.400');
    
    return (
        <VStack align="start" spacing={1} minW="320px">
            {orderId && (
                <Text fontSize="11px" fontWeight="900" color={orderIdColor} letterSpacing="0.05em" mb={0.5}>
                    #{orderId.toUpperCase()}
                </Text>
            )}
            {/* Route Points Horizontal */}
            <HStack spacing={3} align="center">
                <HStack spacing={2} align="center">
                    <Flex align="center" justify="center" minW="22px" h="22px" borderRadius="full" bg="brand.500" color="white" fontSize="10px" fontWeight="900">A</Flex>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>
                        {fromCity?.toUpperCase()} {fromPostcode}
                    </Text>
                </HStack>
                
                <Icon as={MdArrowForward} color="gray.400" boxSize={3.5} />
                
                <HStack spacing={2} align="center">
                    <Flex align="center" justify="center" minW="22px" h="22px" borderRadius="full" bg="red.500" color="white" fontSize="10px" fontWeight="900">B</Flex>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>
                        {toCity?.toUpperCase()} {toPostcode}
                    </Text>
                </HStack>
            </HStack>

            {/* Addresses Grouped Below */}
            {(fromAddress || toAddress) && (
                <VStack align="start" spacing={0} pl="2px" pt={0.5}>
                    {fromAddress && (
                        <Text fontSize="xs" color={mutedText} fontWeight="600" lineHeight="1.2">
                            {fromAddress}
                        </Text>
                    )}
                    {toAddress && (
                        <Text fontSize="xs" color={mutedText} fontWeight="600" lineHeight="1.2">
                            {toAddress}
                        </Text>
                    )}
                </VStack>
            )}

            {/* Posted Date */}
            {postedDate && (
                <Text fontSize="10px" color={mutedText} fontWeight="700" pt={1} textTransform="uppercase" letterSpacing="0.02em">
                    {t('Posted on')} {formatListingDate(postedDate)}
                </Text>
            )}
        </VStack>
    );
};

const MetricsCell = ({ views, quotes, showLabels = false }) => {
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    
    if (showLabels) {
        return (
            <HStack spacing={4}>
                <HStack spacing={1.5}>
                    <Text fontSize="10px" fontWeight="800" color={labelColor} textTransform="uppercase">Views</Text>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>{views}</Text>
                </HStack>
                <HStack spacing={1.5}>
                    <Text fontSize="10px" fontWeight="800" color={labelColor} textTransform="uppercase">Quotas</Text>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>{quotes}</Text>
                </HStack>
            </HStack>
        );
    }

    return (
        <HStack spacing={10} justify="center">
            <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>{views}</Text>
            <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }}>{quotes}</Text>
        </HStack>
    );
};

const CargoCell = ({ category, details, weightLabel, facts = [], items = [] }) => {
    const mutedText = useColorModeValue('gray.500', 'whiteAlpha.600');

    return (
        <VStack align="center" spacing={1.5} maxW="450px" w="100%">
            <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }} textTransform="capitalize" textAlign="center">
                {category}
            </Text>
            <VStack align="center" spacing={1} w="100%">
                {items.length > 0 ? (
                    items.map((item, idx) => (
                        <Text key={idx} fontSize="md" color="navy.700" _dark={{ color: 'whiteAlpha.900' }} fontWeight="700" noOfLines={1} lineHeight="1.2" textAlign="center">
                            {item.itemDescription || [item.make, item.model].filter(Boolean).join(' ') || item.category}
                        </Text>
                    ))
                ) : (
                    <Text fontSize="md" color="navy.700" _dark={{ color: 'whiteAlpha.900' }} fontWeight="700" noOfLines={1} textAlign="center">
                        {details}
                    </Text>
                )}
                {facts.length > 0 && (
                    <Box mt={2}>
                        {facts.filter(fact => fact.includes('kg') || fact.includes('items')).map((fact, idx) => (
                            <Text key={idx} fontSize="xs" color={mutedText} fontWeight="600" noOfLines={1} textAlign="center">
                                {fact}
                            </Text>
                        ))}
                    </Box>
                )}
            </VStack>
        </VStack>
    );
};

const BudgetCell = ({ amount, currency = 'EUR', isCompact = false }) => {
    const currencyMap = { EUR: '€', USD: '$', GBP: '£' };
    const symbol = currencyMap[currency] || currency;

    if (!amount || amount === 0) {
        return <Text fontWeight="900" color="gray.400" fontSize={isCompact ? "sm" : "md"} textAlign="center">—</Text>;
    }

    return (
        <Text fontWeight="900" color="brand.700" _dark={{ color: 'brand.300' }} fontSize="md" letterSpacing="-0.02em" textAlign="center">
            {symbol}{Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
    );
};

// --- Responsive Dashboard Components ---

const ListHeader = () => {
    const { t } = useTranslation();
    const bg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
    const labelColor = useColorModeValue('gray.500', 'gray.400');

    return (
        <Grid
            display={{ base: 'none', lg: 'grid' }}
            templateColumns={{
                lg: '1.4fr 120px 130px 80px',
                xl: '2fr 130px 1.4fr 140px 100px',
                '2xl': '2fr 130px 1.5fr 90px 90px 140px 100px'
            }}
            gap={{ base: 4, lg: 6 }}
            px={{ base: 4, lg: 6 }}
            py={3}
            bg={bg}
            borderBottom="1px solid"
            borderColor={borderColor}
            borderTopLeftRadius="20px"
            borderTopRightRadius="20px"
        >
            <Text fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Route')}</Text>
            <Text textAlign="center" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Status')}</Text>
            <Text display={{ base: 'none', xl: 'block' }} textAlign="center" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Cargo Details')}</Text>
            <Text display={{ base: 'none', '2xl': 'block' }} textAlign="center" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Views')}</Text>
            <Text display={{ base: 'none', '2xl': 'block' }} textAlign="center" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Quotas')}</Text>
            <Text textAlign="center" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Budget')}</Text>
            <Text textAlign="right" fontSize="10px" fontWeight="800" color={labelColor} letterSpacing="0.05em" textTransform="uppercase">{t('Actions')}</Text>
        </Grid>
    );
};

const ResponsiveListingRow = ({ listing, onDetails }) => {
    const { t } = useTranslation();
    const id = listing._id || listing.id;
    const itemSummary = getItemSummary(listing);
    const weightLabel = listing.totalWeight ? t('{{weight}} kg', { weight: listing.totalWeight }) : itemSummary.facts.find(f => f.includes('kg'));
    
    const fullFrom = [listing.fromAddress, listing.fromCity, listing.fromPostcode, listing.fromCountry].filter(Boolean).join(', ');
    const fullTo = [listing.toAddress, listing.toCity, listing.toPostcode, listing.toCountry].filter(Boolean).join(', ');

    const acceptedQuote = listing.acceptedQuote || listing.quotes?.find(q => q._id === listing.acceptedQuoteId || q.id === listing.acceptedQuoteId);
    const displayBudget = 
        listing.clientPriceGross || 
        acceptedQuote?.grossPrice || 
        acceptedQuote?.price ||
        listing.bestOffer ||
        listing.targetBudget || 
        0;

    const rowBorder = useColorModeValue('gray.50', 'whiteAlpha.50');
    const hoverBg = useColorModeValue('brand.50', 'whiteAlpha.50');

    return (
        <Box
            w="100%"
            px={{ base: 4, lg: 6 }}
            py={{ base: 4, lg: 4 }}
            borderBottom="1px solid"
            borderColor={rowBorder}
            transition="all 0.2s"
            _hover={{ bg: hoverBg }}
            role="group"
        >
            <Grid
                templateColumns={{
                    lg: '1.4fr 120px 130px 80px',
                    xl: '2fr 130px 1.4fr 140px 100px',
                    '2xl': '2fr 130px 1.5fr 90px 90px 140px 100px'
                }}
                gap={{ base: 4, lg: 6 }}
                alignItems="center"
            >
                {/* 1. ROUTE (Includes ID and Posted Date) */}
                <Box minW={0}>
                    <RouteCell
                        fromCity={listing.fromCity}
                        fromPostcode={listing.fromPostcode}
                        toCity={listing.toCity}
                        toPostcode={listing.toPostcode}
                        fromAddress={fullFrom}
                        toAddress={fullTo}
                        orderId={listing.humanId}
                        postedDate={listing.createdAt}
                    />
                </Box>

                {/* 2. STATUS */}
                <VStack spacing={1} textAlign="center" minW={{ lg: '100px', xl: '130px' }}>
                    <StatusBadge status={listing.status} fontSize="11px" px={2.5} py={1.5} mx="auto" />
                </VStack>

                {/* 3. CARGO (Shown on XL+, on LG it's in a sub-row) */}
                <Box display={{ base: 'none', xl: 'flex' }} justifyContent="center">
                    <CargoCell
                        category={formatCategory(listing.category)}
                        details={itemSummary.title}
                        weightLabel={weightLabel}
                        facts={itemSummary.facts}
                        items={listing.items}
                    />
                </Box>

                {/* 4. METRICS (Only Ultra-Wide) */}
                <Box display={{ base: 'none', '2xl': 'block' }}>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }} textAlign="center">
                        {listing.viewCount ?? 0}
                    </Text>
                </Box>
                <Box display={{ base: 'none', '2xl': 'block' }}>
                    <Text fontWeight="800" fontSize="md" color="navy.800" _dark={{ color: 'white' }} textAlign="center">
                        {listing.quotes?.length ?? 0}
                    </Text>
                </Box>

                {/* 5. BUDGET */}
                <Box textAlign="center">
                    <BudgetCell amount={displayBudget} isCompact={false} />
                </Box>

                {/* 6. ACTIONS */}
                <Box textAlign="right">
                    <Button 
                        size="sm" 
                        variant="brand" 
                        px={6}
                        h="36px"
                        fontSize="xs"
                        borderRadius="12px"
                        onClick={() => onDetails(id)}
                    >
                        {t('View')}
                    </Button>
                </Box>
            </Grid>
            
            {/* Tablet/Large Laptop Responsive Sub-row */}
            <Flex 
                display={{ base: 'none', lg: 'flex', xl: 'none' }} 
                mt={3} 
                pt={2} 
                borderTop="1px dashed" 
                borderColor="gray.100"
                _dark={{ borderColor: 'whiteAlpha.100' }}
                justify="space-between"
                align="center"
            >
                <HStack spacing={6}>
                    <CargoCell
                        category={formatCategory(listing.category)}
                        details={itemSummary.title}
                        weightLabel={weightLabel}
                        facts={itemSummary.facts}
                        items={listing.items}
                    />
                </HStack>
                
                <HStack spacing={4}>
                    <MetricsCell
                        views={listing.viewCount ?? 0}
                        quotes={listing.quotes?.length ?? 0}
                        showLabels={true}
                    />
                </HStack>
            </Flex>
        </Box>
    );
};

// --- listing Grid Item (Mobile Card) ---
function ListingGridItem({ listing, archived, onDetails, onRemove }) {
    const { t } = useTranslation();
    const { panelBorder } = useUnifiedSurfaceStyles();
    const { transition, hoverProps } = useUnifiedInteractiveCardStyles();
    const titleColor = useColorModeValue('navy.800', 'white');
    const itemSummary = getItemSummary(listing);
    const listingId = listing._id || listing.id;
    const displayId = listing.humanId || String(listingId).slice(-6).toUpperCase();

    return (
        <UnifiedSurfaceCard p={4} transition={transition} _hover={hoverProps}>
            <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex="1">
                        <StatusBadge status={listing.status} fontSize="11px" px={2} />
                        <Text fontWeight="900" color={titleColor} fontSize="md" noOfLines={1}>
                            {listing.title || t('Listing {{id}}', { id: displayId })}
                        </Text>
                        <Text fontSize="xs" color="gray.500" fontWeight="700" noOfLines={1}>
                            {itemSummary.title} {itemSummary.facts.includes('kg') ? `• ${itemSummary.facts.find(f => f.includes('kg'))}` : ''}
                        </Text>
                    </VStack>
                    <Badge colorScheme="brand" fontSize="11px">#{displayId}</Badge>
                </Flex>

                <VisualRoute
                    from={listing.fromCity}
                    to={listing.toCity}
                    fromDate={formatListingDate(listing.pickupDateFrom)}
                    toDate={formatListingDate(listing.deliveryDateFrom)}
                />

                <HStack justify="space-between" pt={2} borderTop="1px solid" borderColor={panelBorder}>
                    <HStack spacing={6}>
                        <VStack align="center" spacing={0}>
                            <Text fontSize="11px" color="gray.500" fontWeight="900" textTransform="uppercase">{t('Views')}</Text>
                            <Text fontWeight="800" fontSize="md">{listing.viewCount ?? 0}</Text>
                        </VStack>
                        <VStack align="center" spacing={0}>
                            <Text fontSize="11px" color="gray.500" fontWeight="900" textTransform="uppercase">{t('Quotas')}</Text>
                            <Text fontWeight="800" fontSize="md">{listing.quotes?.length ?? 0}</Text>
                        </VStack>
                    </HStack>
                    <VStack align="end" spacing={0}>
                        <Text fontSize="11px" color="gray.500" fontWeight="900" textTransform="uppercase">{t('Budget')}</Text>
                        <BudgetCell amount={listing.clientPriceGross || listing.targetBudget || 0} isCompact={false} />
                    </VStack>
                    <Button size="sm" variant="brand" onClick={() => onDetails(listingId)}>{t('View')}</Button>
                </HStack>
            </VStack>
        </UnifiedSurfaceCard>
    );
}

function GridListingCard({ listing, archived, onDetails, onRemove }) {
    return <ListingGridItem listing={listing} archived={archived} onDetails={onDetails} onRemove={onRemove} />;
}

export default function MyListings() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { tabListProps, tabProps } = useUnifiedTabsStyles();
    const { subtleBg, mutedText } = useUnifiedSurfaceStyles();
    const { resetListing } = useListing();

    const [tabIndex, setTabIndex] = useState(0);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const isDesktop = useBreakpointValue({ base: false, lg: true });

    const archived = tabIndex === 1;

    const fetchListings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = statusFilter ? { status: statusFilter } : { archived };
            if (dateFrom) params.dateFrom = dateFrom;
            if (dateTo) params.dateTo = dateTo;
            const data = await getMyListings(params);
            setListings(Array.isArray(data) ? data : data?.listings ?? data?.data ?? []);
        } catch (err) {
            setError(t('Failed to load listings. Please try again.'));
        } finally {
            setLoading(false);
        }
    }, [archived, dateFrom, dateTo, statusFilter, t]);

    useEffect(() => {
        fetchListings();
    }, [fetchListings]);

    const filteredListings = useMemo(
        () =>
            listings.filter(
                (listing) => matchesSearch(listing, search) && (!statusFilter || listing.status === statusFilter),
            ),
        [listings, search, statusFilter],
    );

    const handleTabChange = (nextIndex) => {
        setTabIndex(nextIndex);
        setStatusFilter('');
    };

    const handleStatusChange = (event) => {
        const nextStatus = event.target.value;
        setStatusFilter(nextStatus);
        if (nextStatus) {
            setTabIndex(ARCHIVED_STATUS_SET.has(nextStatus) ? 1 : 0);
        }
    };



    return (
        <PageShell px={{ base: 4, md: 5, xl: 6 }}>
            <Box w="100%" px={0} py={6}>
                <PageHeader
                    title=""
                    description={t("Manage your transport requests, monitor quote activity, and keep active and archived work easy to scan.")}
                    actions={
                        <Button
                            leftIcon={<Icon as={MdAdd} />}
                            variant="brand"
                            borderRadius="14px"
                            onClick={() => {
                                resetListing();
                                navigate('/create-listing');
                            }}
                            w={{ base: '100%', md: 'auto' }}
                        >
                            {t('Create listing')}
                        </Button>
                    }
                />

                <UnifiedSurfaceCard
                    mt={6}
                    p={{ base: 4, md: 5 }}
                    position="sticky"
                    top={{ base: '92px', md: '82px' }}
                    zIndex={10}
                    backdropFilter="blur(14px)"
                    bg={useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(13, 23, 53, 0.8)')}
                    boxShadow="sm"
                >
                    <VStack align="stretch" spacing={4}>
                        <Flex justify="space-between" align={{ base: 'stretch', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} gap={4}>
                            <Tabs index={tabIndex} onChange={handleTabChange} variant="unstyled">
                                <TabList {...tabListProps}>
                                    <Tab {...tabProps}>{t('Active')}</Tab>
                                    <Tab {...tabProps}>{t('Archive')}</Tab>
                                </TabList>
                            </Tabs>

                            <HStack spacing={2} flexWrap="wrap">
                                <Badge bg={subtleBg} color="brand.600" borderRadius="full" px={3} py={1.5}>
                                    {t('{{count}} visible', { count: filteredListings.length })}
                                </Badge>
                                <Badge colorScheme="brand" borderRadius="full" px={3} py={1.5}>
                                    {t('{{count}} total', { count: listings.length })}
                                </Badge>
                            </HStack>
                        </Flex>

                        <UnifiedFilterBar>
                            <InputGroup flex={{ base: '1 1 100%', lg: '1 1 280px' }} minW={{ lg: '280px' }}>
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={MdSearch} color={mutedText} />
                                </InputLeftElement>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={t("Search by title, route, category, or item")}
                                    borderRadius="14px"
                                    bg="whiteAlpha.700"
                                />
                            </InputGroup>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                flex={{ base: '1 1 100%', lg: '0 0 220px' }}
                                minW={{ lg: '220px' }}
                                borderRadius="14px"
                                bg="whiteAlpha.700"
                            >
                                <option value="">{t('All statuses')}</option>
                                <optgroup label={t("Active statuses")}>
                                    {ACTIVE_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {t(option.label)}
                                        </option>
                                    ))}
                                </optgroup>
                                <optgroup label={t("Archived statuses")}>
                                    {ARCHIVED_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {t(option.label)}
                                        </option>
                                    ))}
                                </optgroup>
                            </Select>
                                <HStack
                                    spacing={2}
                                    px={3}
                                    py={2}
                                    borderRadius="14px"
                                    bg="whiteAlpha.700"
                                    border="1px solid"
                                    borderColor="whiteAlpha.200"
                                >
                                    <Icon as={MdCalendarToday} color="brand.500" />
                                    <Input
                                        type="date"
                                        size="sm"
                                        variant="unstyled"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        maxW="132px"
                                    />
                                </HStack>
                                <HStack
                                    spacing={2}
                                    px={3}
                                    py={2}
                                    borderRadius="14px"
                                    bg="whiteAlpha.700"
                                    border="1px solid"
                                    borderColor="whiteAlpha.200"
                                >
                                    <Icon as={MdCalendarToday} color="blue.500" />
                                    <Input
                                        type="date"
                                        size="sm"
                                        variant="unstyled"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        maxW="132px"
                                    />
                                </HStack>
                                <IconButton
                                    icon={<Icon as={MdRefresh} />}
                                    aria-label={t("Refresh listings")}
                                    variant="outline"
                                    colorScheme="brand"
                                    borderRadius="14px"
                                    onClick={fetchListings}
                                    isLoading={loading}
                                />
                                {(search || dateFrom || dateTo || statusFilter) ? (
                                    <Button
                                        variant="ghost"
                                        color={mutedText}
                                        borderRadius="14px"
                                        onClick={() => {
                                            setSearch('');
                                            setDateFrom('');
                                            setDateTo('');
                                            setStatusFilter('');
                                        }}
                                    >
                                        {t('Clear')}
                                    </Button>
                                ) : null}
                        </UnifiedFilterBar>
                    </VStack>
                </UnifiedSurfaceCard>

                <Box mt={6}>
                    {loading ? (
                        <UnifiedLoadingState label={t("Loading listings...")} minH="320px" />
                    ) : filteredListings.length === 0 ? (
                        <UnifiedEmptyState
                            title={archived ? t('No archived listings yet') : t('No active listings yet')}
                            description={
                                search || dateFrom || dateTo || statusFilter
                                    ? t('No listings match the current filters. Try broadening the search or date range.')
                                    : archived
                                        ? t('Canceled and completed work will appear here once listings move out of the active view.')
                                        : t('Create your next transport request and it will appear here with route, item, and quote activity.')
                            }
                            action={
                                !archived && !search && !dateFrom && !dateTo && !statusFilter ? (
                                    <Button
                                        variant="brand"
                                        borderRadius="14px"
                                        leftIcon={<Icon as={MdAdd} />}
                                        onClick={() => {
                                            resetListing();
                                            navigate('/create-listing');
                                        }}
                                    >
                                        {t('Create listing')}
                                    </Button>
                                ) : null
                            }
                        />
                    ) : isDesktop ? (
                        <UnifiedSurfaceCard overflow="hidden" p={0}>
                            <VStack align="stretch" spacing={0}>
                                <ListHeader />
                                {filteredListings.map((listing) => (
                                    <ResponsiveListingRow
                                        key={listing._id || listing.id}
                                        listing={listing}
                                        onDetails={(id) => navigate(`/my-listings/${id}`)}
                                    />
                                ))}
                            </VStack>
                        </UnifiedSurfaceCard>
                    ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                            {filteredListings.map((listing) => (
                                <GridListingCard
                                    key={listing._id || listing.id}
                                    listing={listing}
                                    archived={archived}
                                    onDetails={(id) => navigate(`/my-listings/${id}`)}
                                />
                            ))}
                        </SimpleGrid>
                    )}
                </Box>
            </Box>


        </PageShell>
    );
}
